import { useState, useCallback, useEffect } from 'react';
import { Button, TextField, Select, Tag } from '@cimpress/react-components';
import {
  IconPencilAlt,
  IconBin,
} from '@cimpress-technology/react-streamline-icons';
import { DATA_TYPES, FIELD_STYLE } from '../constants';
import { Attribute } from '../models/EditableList';
import ActionButtons from './shared/ActionButtons';
import styles from '../css/pages.module.scss';
import { generateId, toCamelCase, toDisplayName } from '../utilities/common';

interface AttributesTabProps {
  attributes: Attribute[];
  onChange: (attributes: Attribute[]) => void;
  readOnlyIds?: string[];
}

interface AttributeInputRowProps {
  onAdd: (name: string, type: string) => void;
  onCancel: () => void;
  existingNames: string[];
  depth?: number;
  allowedTypes?: { value: string; label: string }[];
  isNested?: boolean;
}

interface AttributeRowProps {
  attribute: Attribute;
  onDelete: (id: string) => void;
  onUpdate: (id: string, name: string, type: string) => void;
  onAddChild: (parentId: string, name: string, type: string) => void;
  onDeleteChild: (parentId: string, childId: string) => void;
  onUpdateChild: (
    parentId: string,
    childId: string,
    name: string,
    type: string,
  ) => void;
  existingNames: string[];
  depth?: number;
  getAllDescendantNames: (attr: Attribute) => string[];
  isNested?: boolean;
  isReadOnly?: boolean;
  readOnlyIds?: string[];
}

// Attribute Input Row Component
const AttributeInputRow = ({
  onAdd,
  onCancel,
  existingNames,
  depth = 0,
  allowedTypes = DATA_TYPES,
  isNested = false,
}: AttributeInputRowProps) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<{ value: string; label: string } | null>(
    allowedTypes.length === 1 ? allowedTypes[0] : null,
  );
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const normalizedName = toCamelCase(name);

    if (existingNames.includes(normalizedName)) {
      setError('Name already exists');
      return;
    }
    if (!type) {
      setError('Data type is required');
      return;
    }
    onAdd(normalizedName.trim(), type.value);
    setName('');
    setType(allowedTypes.length === 1 ? allowedTypes[0] : null);
    setError(null);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    if (existingNames.includes(toCamelCase(newName.trim()))) {
      setError('Name already exists');
    } else {
      setError(null);
    }
  };

  return (
    <div
      className={`${styles.attributeInputRow} ${isNested ? styles.nestedAttributeInputRow : ''}`}
      style={{ marginLeft: `${depth * 32}px` }}
    >
      <div className={styles.attributeInputFields}>
        <TextField
          label="Attribute Name"
          value={name}
          onChange={handleNameChange}
          required
          status={error ? 'error' : undefined}
          helpText={error}
          style={FIELD_STYLE}
        />
        <Select
          label="Data Type"
          options={allowedTypes}
          value={type}
          onChange={(selected) => setType(selected)}
          placeholder="Select type..."
          style={FIELD_STYLE}
        />
        <ActionButtons
          onSave={handleAdd}
          onCancel={onCancel}
          disabled={!name.trim() || !type || !!error}
        />
      </div>
    </div>
  );
};

// Single Attribute Row Component (displays saved attribute)
const AttributeRow = ({
  attribute,
  onDelete,
  onUpdate,
  onAddChild,
  onDeleteChild,
  onUpdateChild,
  existingNames,
  depth = 0,
  getAllDescendantNames,
  isNested = false,
  isReadOnly = false,
  readOnlyIds = [],
}: AttributeRowProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showChildInput, setShowChildInput] = useState(true);
  const [editName, setEditName] = useState(attribute.name);
  const [editType, setEditType] = useState<{
    value: string;
    label: string;
  } | null>(DATA_TYPES.find((dt) => dt.value === attribute.type) || null);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    setEditName(attribute.name);
    setEditType(DATA_TYPES.find((dt) => dt.value === attribute.type) || null);
  }, [attribute.name, attribute.type]);

  const handleEditSave = () => {
    if (!editName.trim()) {
      setEditError('Name is required');
      return;
    }

    const normalizedName = toCamelCase(editName);

    const otherNames = existingNames.filter(
      (n) => n !== toCamelCase(attribute.name),
    );

    if (otherNames.includes(normalizedName)) {
      setEditError('Name already exists');
      return;
    }

    if (!editType) {
      setEditError('Data type is required');
      return;
    }

    onUpdate(attribute.id, normalizedName, editType.value);
    setIsEditing(false);
    setEditError(null);
  };

  const handleEditCancel = () => {
    setEditName(toDisplayName(attribute.name));
    setEditType(DATA_TYPES.find((dt) => dt.value === attribute.type) || null);
    setIsEditing(false);
    setEditError(null);
  };

  const handleEditNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setEditName(newName);
    const otherNames = existingNames.filter(
      (n) => n !== attribute.name.toLowerCase(),
    );
    if (otherNames.includes(newName.trim().toLowerCase())) {
      setEditError('Name already exists');
    } else {
      setEditError(null);
    }
  };

  // Get existing names for children (excluding descendants of current attribute)
  const childExistingNames = attribute.children
    ? attribute.children.map((c) => toCamelCase(c.name))
    : [];

  const handleAddChild = (name: string, type: string) => {
    onAddChild(attribute.id, name, type);
    setShowChildInput(false);
  };

  const handleDeleteChild = (childId: string) => {
    onDeleteChild(attribute.id, childId);
  };

  const handleUpdateChild = (childId: string, name: string, type: string) => {
    onUpdateChild(attribute.id, childId, name, type);
  };

  const editAllowedTypes = DATA_TYPES;

  if (isEditing) {
    return (
      <div
        className={styles.attributeInputRow}
        style={{ marginLeft: `${depth * 32}px` }}
      >
        <div className={styles.attributeInputFields}>
          <TextField
            label="Attribute Name"
            value={editName}
            onChange={handleEditNameChange}
            required
            status={editError ? 'error' : undefined}
            helpText={editError}
            style={FIELD_STYLE}
          />
          <Select
            label="Data Type"
            options={editAllowedTypes}
            value={editType}
            onChange={(selected) => setEditType(selected)}
            placeholder="Select type..."
            style={FIELD_STYLE}
          />
          <ActionButtons
            onSave={handleEditSave}
            onCancel={handleEditCancel}
            disabled={!editName.trim() || !editType || !!editError}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`${styles.attributeRow} ${isNested ? styles.nestedAttributeRow : ''}`}
        style={{ marginLeft: `${depth * 32}px` }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={styles.attributeRowContent}>
          <span className={styles.attributeName}>
            {toDisplayName(attribute.name)}
          </span>
          <Tag value={attribute.type} className={styles.attributeTypeTag} />
        </div>
        <div
          className={styles.attributeActions}
          style={{
            visibility: isHovered && !isReadOnly ? 'visible' : 'hidden',
          }}
        >
          <Button variant="anchor" onClick={() => setIsEditing(true)}>
            <IconPencilAlt size="2x" />
          </Button>
          <Button variant="anchor" onClick={() => onDelete(attribute.id)}>
            <IconBin size="2x" />
          </Button>
        </div>
      </div>

      {/* Render children for object and array types */}
      {(attribute.type === 'object' || attribute.type === 'array') && (
        <div
          className={styles.attributeChildren}
          style={{ marginLeft: `${(depth + 1) * 32}px` }}
        >
          <div className={styles.attributeChildrenContent}>
            {attribute.children?.map((child) => (
              <AttributeRow
                key={child.id}
                attribute={child}
                onDelete={handleDeleteChild}
                onUpdate={handleUpdateChild}
                onAddChild={onAddChild}
                onDeleteChild={onDeleteChild}
                onUpdateChild={onUpdateChild}
                existingNames={childExistingNames}
                depth={0}
                getAllDescendantNames={getAllDescendantNames}
                isNested={true}
                isReadOnly={readOnlyIds.includes(child.id)}
                readOnlyIds={readOnlyIds}
              />
            ))}
            {showChildInput && (
              <AttributeInputRow
                onAdd={handleAddChild}
                onCancel={() => setShowChildInput(false)}
                existingNames={childExistingNames}
                depth={0}
                allowedTypes={DATA_TYPES}
                isNested={true}
              />
            )}
            <Button
              variant="default"
              className={styles.addChildButton}
              onClick={() => setShowChildInput(true)}
              disabled={showChildInput}
            >
              + Add
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

// Main Attributes Tab Component
const AttributesTab = ({
  attributes,
  onChange,
  readOnlyIds = [],
}: AttributesTabProps) => {
  const [showInput, setShowInput] = useState(true);

  // Get all attribute names at the root level for uniqueness validation
  const getRootLevelNames = useCallback(() => {
    return attributes.map((attr) => toCamelCase(attr.name));
  }, [attributes]);

  // Get all descendant names recursively
  const getAllDescendantNames = useCallback((attr: Attribute): string[] => {
    const names: string[] = [attr.name.toLowerCase()];
    if (attr.children) {
      attr.children.forEach((child) => {
        names.push(...getAllDescendantNames(child));
      });
    }
    return names;
  }, []);

  // Add new root-level attribute
  const handleAddAttribute = (name: string, type: string) => {
    const newAttribute: Attribute = {
      id: generateId('attr'),
      name: toCamelCase(name),
      type,
      children: type === 'object' || type === 'array' ? [] : undefined,
    };
    onChange([...attributes, newAttribute]);
    setShowInput(false);
  };

  // Delete root-level attribute
  const handleDeleteAttribute = (id: string) => {
    onChange(attributes.filter((attr) => attr.id !== id));
  };

  // Update root-level attribute
  const handleUpdateAttribute = (id: string, name: string, type: string) => {
    onChange(
      attributes.map((attr) => {
        if (attr.id === id) {
          return {
            ...attr,
            name,
            type,
            children:
              type === 'object' || type === 'array'
                ? attr.children || []
                : undefined,
          };
        }
        return attr;
      }),
    );
  };

  // Recursive helper to add child at any depth
  const addChildToAttribute = (
    attrs: Attribute[],
    parentId: string,
    name: string,
    type: string,
  ): Attribute[] => {
    return attrs.map((attr) => {
      if (attr.id === parentId) {
        const newChild: Attribute = {
          id: generateId('attr'),
          name,
          type,
          children: type === 'object' || type === 'array' ? [] : undefined,
        };
        return {
          ...attr,
          children: [...(attr.children || []), newChild],
        };
      }
      if (attr.children) {
        return {
          ...attr,
          children: addChildToAttribute(attr.children, parentId, name, type),
        };
      }
      return attr;
    });
  };

  // Recursive helper to delete child at any depth
  const deleteChildFromAttribute = (
    attrs: Attribute[],
    parentId: string,
    childId: string,
  ): Attribute[] => {
    return attrs.map((attr) => {
      if (attr.id === parentId) {
        return {
          ...attr,
          children: attr.children?.filter((child) => child.id !== childId),
        };
      }
      if (attr.children) {
        return {
          ...attr,
          children: deleteChildFromAttribute(attr.children, parentId, childId),
        };
      }
      return attr;
    });
  };

  // Recursive helper to update child at any depth
  const updateChildInAttribute = (
    attrs: Attribute[],
    parentId: string,
    childId: string,
    name: string,
    type: string,
  ): Attribute[] => {
    return attrs.map((attr) => {
      if (attr.id === parentId) {
        return {
          ...attr,
          children: attr.children?.map((child) => {
            if (child.id === childId) {
              return {
                ...child,
                name,
                type,
                children:
                  type === 'object' || type === 'array'
                    ? child.children || []
                    : undefined,
              };
            }
            return child;
          }),
        };
      }
      if (attr.children) {
        return {
          ...attr,
          children: updateChildInAttribute(
            attr.children,
            parentId,
            childId,
            name,
            type,
          ),
        };
      }
      return attr;
    });
  };

  const handleAddChild = (parentId: string, name: string, type: string) => {
    onChange(addChildToAttribute(attributes, parentId, name, type));
  };

  const handleDeleteChild = (parentId: string, childId: string) => {
    onChange(deleteChildFromAttribute(attributes, parentId, childId));
  };

  const handleUpdateChild = (
    parentId: string,
    childId: string,
    name: string,
    type: string,
  ) => {
    onChange(updateChildInAttribute(attributes, parentId, childId, name, type));
  };

  return (
    <div className={styles.attributesTabContainer}>
      <div className={styles.attributesHeader}>
        <span>Total {attributes.length} Attributes</span>
      </div>

      <div className={styles.attributesList}>
        {attributes.map((attr) => (
          <AttributeRow
            key={attr.id}
            attribute={attr}
            onDelete={handleDeleteAttribute}
            onUpdate={handleUpdateAttribute}
            onAddChild={handleAddChild}
            onDeleteChild={handleDeleteChild}
            onUpdateChild={handleUpdateChild}
            existingNames={getRootLevelNames()}
            depth={0}
            getAllDescendantNames={getAllDescendantNames}
            isReadOnly={readOnlyIds.includes(attr.id)}
            readOnlyIds={readOnlyIds}
          />
        ))}
      </div>

      {showInput && (
        <AttributeInputRow
          onAdd={handleAddAttribute}
          onCancel={() => setShowInput(false)}
          existingNames={getRootLevelNames()}
        />
      )}

      <Button
        variant="default"
        className={styles.addButton}
        onClick={() => setShowInput(true)}
        disabled={showInput}
      >
        + Add
      </Button>
    </div>
  );
};

export default AttributesTab;
